import { isObject, isEmpty, isNumber, isError, isString } from '#library/assertion.library.js';
import { enumify } from '#library/enumerate.library.js';
import { sym } from '#library/symbol.library.js';
export const LOG = enumify(['Off', 'Error', 'Warn', 'Info', 'Debug', 'Trace']);
const Method = {
    Off: 'off',
    Log: 'log',
    Info: 'info',
    Warn: 'warn',
    Debug: 'debug',
    Trace: 'trace',
    Error: 'error',
};
const Level = {
    [Method.Off]: LOG.Off,
    [Method.Error]: LOG.Error,
    [Method.Warn]: LOG.Warn,
    [Method.Info]: LOG.Info,
    [Method.Log]: LOG.Info,
    [Method.Debug]: LOG.Debug,
    [Method.Trace]: LOG.Trace,
};
/**
 * Parses a debug level (numeric or string) into a strongly-typed LOG enumeration value.
 *
 * @param level - The log level to parse
 * @param fallback - The default log level to return if parsing fails (default: Info)
 * @returns The resolved LOG enumeration value
 * @example
 * ```ts
 * parseLogLevel('debug'); // LOG.Debug
 * ```
 */
export function parseLogLevel(level, fallback = LOG.Info) {
    if (isNumber(level))
        return (level >= LOG.Off && level <= LOG.Trace) ? level : fallback;
    if (isString(level))
        return Level[level.toLowerCase()] ?? fallback;
    return fallback;
}
/**
 * A lightweight, dependency-free namespaced logger.
 * Decoupled from error handling and boundaries, utilizing standard `console` methods.
 * Supports configurable log levels and structured object output.
 *
 * @example
 * ```ts
 * const log = new Logger('App', LOG.Debug);
 * log.info('Started'); // [App] Started
 * ```
 */
export class Logger {
    #namespace;
    /** The current verbosity level */
    level;
    constructor(namespace, level = LOG.Info) {
        this.#namespace = namespace.startsWith('[') ? namespace : `[${namespace}]`;
        this.level = level;
    }
    #emit(method, ...msg) {
        let config;
        if (msg.length > 0 && isObject(msg[0])) {
            try {
                if (msg[0][sym.$LogConfig]) {
                    config = msg.shift();
                }
            }
            catch {
                // Ignore access errors on proxies/getters
            }
        }
        let activeLevel = this.level;
        if (config) {
            if (config.silent === true)
                return;
            if (config.debug !== undefined) {
                activeLevel = parseLogLevel(config.debug, activeLevel);
            }
        }
        if (activeLevel === LOG.Off)
            return;
        const methodLevel = Level[method] ?? 0;
        if (methodLevel > activeLevel)
            return;
        const output = msg
            .map(m => {
            if (isError(m))
                return m.message;
            if (isObject(m)) {
                try {
                    const name = m.constructor?.name ?? 'Object';
                    if (name === 'Object') {
                        const keys = Object.keys(m);
                        const summary = keys.slice(0, 3).join(', ');
                        return `{ ${summary}${keys.length > 3 ? `, ... (+${keys.length - 3} more)` : ''} }`;
                    }
                    return `[${name}]`;
                }
                catch {
                    return '[Object]';
                }
            }
            return String(m);
        })
            .filter(s => !isEmpty(s)).join(' ');
        if (!isEmpty(output)) {
            const consoleMethod = method;
            console[consoleMethod](`${this.#namespace} ${output}`);
        }
    }
    /** console.log */ log = (...msg) => this.#emit(Method.Log, ...msg);
    /** console.info */ info = (...msg) => this.#emit(Method.Info, ...msg);
    /** console.warn */ warn = (...msg) => this.#emit(Method.Warn, ...msg);
    /** console.debug */ debug = (...msg) => this.#emit(Method.Debug, ...msg);
    /** console.trace */ trace = (...msg) => this.#emit(Method.Trace, ...msg);
    /** console.error */ error = (...msg) => this.#emit(Method.Error, ...msg);
}
